import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Divider,
  IconButton,
  Button,
  Grid,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  AttachFile as AttachFileIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { getCalledDescriptionById } from '../../services/called.service';
import { getFileDownloadLink } from '../../services/attachment.service';
import Api from '../../services/api';

const TicketDetailModal = ({ ticket, open, onClose, getStatusConfig, lang }) => {
  const [description,        setDescription]        = useState('');
  const [loadingDescription, setLoadingDescription] = useState(false);
  const [calledDetails,      setCalledDetails]      = useState(null);
  const [loadingDetails,     setLoadingDetails]     = useState(false);
  const [downloadingId,      setDownloadingId]      = useState(null);

  useEffect(() => {
    if (!open || !ticket?.id) { setDescription(''); return; }
    let cancelled = false;
    setLoadingDescription(true);
    getCalledDescriptionById(ticket.id)
      .then(res => { if (!cancelled) setDescription(res?.data?.data ?? ''); })
      .catch(() => { if (!cancelled) setDescription(''); })
      .finally(() => { if (!cancelled) setLoadingDescription(false); });
    return () => { cancelled = true; };
  }, [open, ticket?.id]);

  useEffect(() => {
    if (!open || !ticket?.id) { setCalledDetails(null); return; }
    let cancelled = false;
    setLoadingDetails(true);
    Api.get(`/calleds/treat-called/${ticket.id}`)
      .then(res => { if (!cancelled) setCalledDetails(res?.data?.data ?? null); })
      .catch(() => { if (!cancelled) setCalledDetails(null); })
      .finally(() => { if (!cancelled) setLoadingDetails(false); });
    return () => { cancelled = true; };
  }, [open, ticket?.id]);

  const handleDownloadAttachment = async (attachment) => {
    setDownloadingId(attachment.id);
    try {
      const res = await getFileDownloadLink(attachment.id);
      if (res?.data?.success && res.data.data) {
        window.open(res.data.data, '_blank');
      }
    } finally {
      setDownloadingId(null);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'alta':  return { bg: '#fee2e2', color: '#ef4444' };
      case 'média': return { bg: '#fff7cd', color: '#f59e0b' };
      case 'baixa': return { bg: '#d1fae5', color: '#10b981' };
      default:      return { bg: '#f3f4f6', color: '#6b7280' };
    }
  };

  if (!open || !ticket) return null;

  const attachments  = calledDetails?.calledattachments       ?? [];
  const customFields = calledDetails?.calledorganizationfields ?? [];

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1300,
        bgcolor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
      onClick={onClose}
    >
      <Box
        sx={{
          bgcolor: '#fff',
          borderRadius: '12px',
          width: '100%',
          maxWidth: 800,
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 24,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Cabeçalho fixo */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Typography variant="h6">Detalhes do Ticket</Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Conteúdo rolável */}
        <Box sx={{ flex: '1 1 auto', overflowY: 'auto', p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: getStatusConfig(ticket.status).color,
                mr: 1,
              }}
            />
            <Typography variant="body1">{ticket.status}</Typography>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Typography variant="h5" sx={{ mb: 1 }}>
            <a
              href={`/${lang}/called-accompaniment/${ticket.id}`}
              target="_blank"
              rel="noreferrer"
              style={{ color: '#1976d2', textDecoration: 'underline' }}
            >
              #{ticket.id} - {ticket.title}
            </a>
          </Typography>

          <Chip
            label={ticket.priority}
            sx={{
              mb: 3,
              bgcolor: getPriorityColor(ticket.priority).bg,
              color:   getPriorityColor(ticket.priority).color,
            }}
          />

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">Cliente</Typography>
              <Typography variant="body1">{ticket.client}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">Categoria</Typography>
              <Typography variant="body1">{ticket.category}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">Responsável</Typography>
              <Typography variant="body1">{ticket.assignedTo || 'Não atribuído'}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">Data de Abertura</Typography>
              <Typography variant="body1">{ticket.openDate}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">Data de Previsão</Typography>
              <Typography variant="body1">{ticket.predictedDate}</Typography>
            </Grid>
          </Grid>

          {/* Descrição */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Descrição</Typography>
          <Paper sx={{ p: 2, bgcolor: '#f9fafb', borderRadius: 2, minHeight: 48, mb: 3 }}>
            {loadingDescription
              ? <CircularProgress size={16} />
              : description
                ? <Box sx={{ '& *': { maxWidth: '100%' }, '& img': { height: 'auto' } }} dangerouslySetInnerHTML={{ __html: description }} />
                : <Typography variant="body1">—</Typography>
            }
          </Paper>

          {/* Anexos */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <AttachFileIcon color="primary" sx={{ mr: 1, fontSize: 20 }} />
              <Typography variant="subtitle1">Anexos</Typography>
            </Box>
            <Divider sx={{ mb: 1.5 }} />
            {loadingDetails ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : attachments.length > 0 ? (
              <Grid container spacing={1.5}>
                {attachments.map((calledAttachment, index) => {
                  const att = calledAttachment.attachment;
                  const isDownloading = downloadingId === att.id;
                  return (
                    <Grid item xs={12} sm={6} md={4} key={calledAttachment.id}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          p: '8px 12px',
                          bgcolor: 'rgba(189,189,189,0.2)',
                          borderRadius: 1,
                          height: '100%',
                        }}
                      >
                        <AttachFileIcon fontSize="small" color="primary" sx={{ mr: 1, flexShrink: 0 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Button
                            onClick={() => handleDownloadAttachment(att)}
                            disabled={isDownloading}
                            sx={{
                              textTransform: 'none',
                              fontSize: '0.85rem',
                              p: 0,
                              pl: '4px',
                              minWidth: 'auto',
                              justifyContent: 'flex-start',
                              width: '100%',
                              textAlign: 'left',
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={att.description || `Anexo ${index + 1}`}
                          >
                            {isDownloading ? <CircularProgress size={12} sx={{ mr: 0.5 }} /> : null}
                            {att.description || `Anexo ${index + 1}`}
                          </Button>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ pl: '4px' }}>
                            {(att.size / 1024).toFixed(2)} KB
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary">Nenhum anexo disponível.</Typography>
            )}
          </Box>

          {/* Campos Personalizados */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <InfoIcon color="primary" sx={{ mr: 1, fontSize: 20 }} />
              <Typography variant="subtitle1">Campos Personalizados</Typography>
            </Box>
            <Divider sx={{ mb: 1.5 }} />
            {loadingDetails ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : customFields.length > 0 ? (
              <Grid container spacing={1.5}>
                {customFields.map((field) => {
                  const rawValue   = field.organizationfieldoptionvalues?.name || field.value;
                  const fieldValue = (rawValue && rawValue !== 'null') ? rawValue : '—';
                  return (
                    <Grid item xs={12} sm={6} key={field.id}>
                      <Box
                        sx={{
                          p: 1.5,
                          bgcolor: '#f9f9f9',
                          borderRadius: 1,
                          borderLeft: '3px solid',
                          borderLeftColor: 'primary.main',
                          height: '100%',
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="primary"
                          display="block"
                          sx={{ textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px', mb: 0.5 }}
                        >
                          {field.organizationfield?.name}
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#333', wordBreak: 'break-word' }}>
                          {fieldValue}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary">Nenhum campo personalizado disponível.</Typography>
            )}
          </Box>
        </Box>

        {/* Rodapé fixo */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
            borderTop: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>
            Fechar
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{ borderRadius: 2 }}
            onClick={() => window.open(`/${lang}/called-accompaniment/${ticket.id}`, '_blank')}
          >
            Editar Ticket
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default TicketDetailModal;
